import { Bloc } from "@lib/bloc/Bloc"
import { Asset } from "@model/assets/Asset"
import {
    ServiceItemForm,
    compareObjectServiceItemForm,
    newServiceItemForm,
} from "@model/serviceRequests/ServiceItemForm"
import { Tag, TagChange, tagChangeFromTag } from "@model/Tag"
import { WorkRequest, WorkRequestStatus, WorkRequestType } from "@model/workRequests/WorkRequest"
import { AssetService } from "@service/assets/AssetService"
import { workRequestToServiceItemForm } from "@service/workRequests/MainWorkRequestService"
import { WorkRequestService } from "@service/workRequests/WorkRequestService"
import { UserBloc } from "@state/user/UserBloc"
import { ServiceItemBloc } from "@state/workRequests/ServiceItemBloc"
import isEqual from "lodash.isequal"

export type ServiceItemsListForm = {
    items: ServiceItemForm[]
    itemsInitialState: ServiceItemForm[]
    serviceItemsHasChanged: boolean
    workRequestToRemove: WorkRequest | null
    workRequestToDismiss: {
        request: WorkRequest
        reason: string
        tagChange: TagChange
        tagReason: string
    } | null
}

export class ServiceItemsBloc extends Bloc<ServiceItemsListForm> {
    private itemBlocs: Map<number, ServiceItemBloc> = new Map()
    private nextId = 1
    onListItemsChange?: (serviceItems: ServiceItemForm[]) => void
    // Bug #11217: fires whenever a Service Item is ADDED (initial load or incrementally), never on
    // remove/update. The new-Work-Order flow uses it to keep the WO Due Date / Odometer / Hour Meter
    // at the closest-to-due value across its items.
    onServiceItemAdded?: (item: ServiceItemForm) => void

    constructor(
        private workRequestService: WorkRequestService,
        private userBlock: UserBloc,
        private assetService: AssetService
    ) {
        super(
            {
                items: [],
                itemsInitialState: [],
                serviceItemsHasChanged: false,
                workRequestToRemove: null,
                workRequestToDismiss: null,
            },
            { persistStateOnDispose: false }
        )
    }

    override computed = (state: ServiceItemsListForm): Partial<ServiceItemsListForm> => {
        return {
            serviceItemsHasChanged: this.serviceItemsHasChanged(),
        }
    }

    areAllServiceItemsInDoneStatus = () => {
        return this.state.items.every((item) => item.status === WorkRequestStatus.Done)
    }

    serviceItemsHasChanged = () => {
        return (
            this.state.itemsInitialState.length !== this.state.items.length ||
            this.state.itemsInitialState.some((item, index) => {
                let currentItem = this.state.items.find((i) => i.uiElementId === item.uiElementId)
                if (!currentItem) return true

                return !isEqual(compareObjectServiceItemForm(item), compareObjectServiceItemForm(currentItem))
            })
        )
    }

    /** Add a new service item */
    addItem = () => {
        const uiElementId = this.nextId++
        const itemForm = newServiceItemForm(uiElementId)
        const itemBloc = new ServiceItemBloc(uiElementId, this.workRequestService, this.userBlock)
        this.itemBlocs.set(uiElementId, itemBloc)
        this.update({ items: [...this.state.items, itemForm] })

        this.onServiceItemAdded?.(itemForm)

        // Always notify parent so it can recalculate formHasChanged
        this.onListItemsChange?.(this.state.items)
    }

    addExistingItem = async (item: ServiceItemForm, isInitialLoad?: boolean): Promise<void> => {
        if (this.state.items.filter((i) => i.id == item.id).length == 0) {
            if (item.tasks?.length == 0 && item.workRequestType !== WorkRequestType.Service) {
                //We fully fetch the work request to get its steps here (only for inspections and preventatives). Previously we only had a summary in the AddExistingServiceItemsDialog
                const itemWithSteps = await this.workRequestService.fetchWorkRequest(item.id!)
                if (itemWithSteps.isError()) throw itemWithSteps.error

                item = workRequestToServiceItemForm(itemWithSteps.value)
            }

            const uiElementId = this.nextId++
            item.uiElementId = uiElementId
            item.isSaved = isInitialLoad ?? false
            const itemBloc = new ServiceItemBloc(uiElementId, this.workRequestService, this.userBlock)
            await itemBloc.setState(item)
            this.itemBlocs.set(uiElementId, itemBloc)
            this.update({ items: [...this.state.items, item] })
            if (isInitialLoad) {
                this.update({ itemsInitialState: [...this.state.itemsInitialState, item] })
            }

            // Bug #11217: recompute the WO due values on every add, including the initial load (so a
            // Work Order created from selected items takes the closest-to-due values across them).
            this.onServiceItemAdded?.(item)

            // Notify parent so it can recalculate formHasChanged (skip during initial load)
            if (!isInitialLoad) {
                this.onListItemsChange?.(this.state.items)
            }
        }
    }

    /** Update an existing item */
    updateItem = (item: ServiceItemForm) => {
        this.update({
            items: this.state.items.map((i) => (i.uiElementId === item.uiElementId ? item : i)),
        })

        // Always notify parent of item updates so it can recalculate formHasChanged.
        // This ensures reverting a change (no net difference) properly disables the Save button.
        this.onListItemsChange?.(this.state.items)
    }

    /** Delete an item (and its attachments) */
    deleteItem = (uiElementId: number) => {
        const bloc = this.itemBlocs.get(uiElementId)
        if (bloc) {
            bloc.clearAttachmentsOnCancel()
            bloc.deleteItem()
            this.itemBlocs.delete(uiElementId)
        }
        this.update({ items: this.state.items.filter((i) => i.uiElementId !== uiElementId) })
        this.onListItemsChange?.(this.state.items)
    }

    /** Clear all items and all attachments (on cancel) */
    clearAll = (deleteAttachments: boolean = false) => {
        if (deleteAttachments) {
            this.itemBlocs.forEach((bloc) => bloc.clearAttachmentsOnCancel())
        }
        this.itemBlocs.clear()
        this.update({ items: [], itemsInitialState: [] })
    }

    /** Get the bloc for a specific item */
    getItemBloc = (uiElementId: number): ServiceItemBloc | undefined => this.itemBlocs.get(uiElementId)

    workRequestToRemoveChanged = (workRequest: WorkRequest | null) =>
        this.update({
            workRequestToRemove: workRequest,
        })

    removeWorkRequest = async (workRequest: WorkRequest) => {
        workRequest.workOrderId = null
        var response = await this.workRequestService.updateWorkRequest(workRequest)
        if (response.isOk()) {
            this.update({
                workRequestToRemove: null,
                items: this.state.items.filter((i) => i.id !== workRequest.id),
            })
        }
    }

    updateAssetTag = async (asset: Asset, tagChange: TagChange, tagReason: string) => {
        const originalTagChange = tagChangeFromTag(asset?.tag ?? null)
        if (asset !== null && originalTagChange !== tagChange) {
            var result = await this.assetService.updateTag(asset.id, tagChange, tagReason)
            if (result.isError()) return null

            return result.value
        }
        return null
    }

    dismissWorkRequest = async (workRequest: WorkRequest, reason: string, userId: number, tag?: Tag | null) => {
        const response = await this.workRequestService.dismissWorkRequest(workRequest.id, reason, userId, tag)
        if (response.isOk()) {
            this.update({
                workRequestToDismiss: null,
                items: this.state.items.filter((i) => i.id !== workRequest.id),
            })
        }

        return response
    }

    workRequestToDismissChanged = (
        request: WorkRequest | null,
        asset: Asset | null,
        change: { reason?: string; tagChange?: TagChange; tagReason?: string }
    ) => {
        this.update({
            workRequestToDismiss:
                request === null
                    ? null
                    : {
                          request: request,
                          reason: change.reason ?? this.state.workRequestToDismiss?.reason ?? "",
                          tagChange:
                              change.tagChange ??
                              this.state.workRequestToDismiss?.tagChange ??
                              tagChangeFromTag(asset?.tag ?? null),
                          tagReason:
                              change.tagReason ??
                              this.state.workRequestToDismiss?.tagReason ??
                              asset?.tag?.reason ??
                              "",
                      },
        })
    }
}
